import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { ShieldCheck, Loader2, RefreshCw, X, Mail, Lock } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";

/**
 * Vinci · Order Security Pulse modal.
 *
 * Renders a 6-box OTP input, ticking TTL, resend cooldown, error state
 * and an "Authorize the Trade" CTA. The parent controls the flow via
 * ``useSecurityPulse`` — this component is a pure UI shell.
 *
 * Props
 * -----
 * open              — bool, dialog visibility
 * onClose           — () => void
 * pulseId           — string · id returned by /security-pulse/issue
 * actionLabel       — string · e.g. "Purchase Order · Checkout"
 * deliveryEmail     — string · masked email echoed in copy
 * expiresAt         — ISO datetime string
 * resendAvailableAt — ISO datetime string
 * verifying         — bool · from parent's async state
 * onVerify(code)    — Promise<void>
 * onResend()        — Promise<void>
 */
export function SecurityPulseModal({
  open, onClose, pulseId, actionLabel, deliveryEmail,
  expiresAt, resendAvailableAt, verifying,
  onVerify, onResend,
}) {
  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const [now, setNow] = useState(Date.now());
  const inputs = useRef([]);

  useEffect(() => {
    if (!open) return;
    setDigits(["", "", "", "", "", ""]);
    const t = setInterval(() => setNow(Date.now()), 1000);
    setTimeout(() => inputs.current[0]?.focus(), 120);
    return () => clearInterval(t);
  }, [open, pulseId]);

  const expiresMs = expiresAt ? new Date(expiresAt).getTime() - now : 0;
  const expired = expiresMs <= 0;
  const resendMs = resendAvailableAt ? new Date(resendAvailableAt).getTime() - now : 0;
  const resendReady = resendMs <= 0;

  const setDigit = (i, v) => {
    v = (v || "").replace(/\D/g, "").slice(0, 1);
    setDigits((prev) => {
      const nx = [...prev]; nx[i] = v; return nx;
    });
    if (v && i < 5) inputs.current[i + 1]?.focus();
  };
  const onKeyDown = (i, e) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) inputs.current[i - 1]?.focus();
  };
  const onPaste = (e) => {
    const t = (e.clipboardData?.getData("text") || "").replace(/\D/g, "").slice(0, 6);
    if (t.length === 6) {
      e.preventDefault();
      setDigits(t.split(""));
      inputs.current[5]?.focus();
    }
  };
  const code = digits.join("");
  const canSubmit = code.length === 6 && !verifying && !expired;

  const submit = async () => {
    if (!canSubmit) return;
    try { await onVerify(code); }
    catch (e) {
      setDigits(["", "", "", "", "", ""]);
      inputs.current[0]?.focus();
    }
  };
  const fmtMS = (ms) => {
    if (ms <= 0) return "00:00";
    const s = Math.max(0, Math.floor(ms / 1000));
    return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="security-pulse-modal"
                      className="max-w-md bg-[#0a0906] border border-[#D8B23E]/40 text-[#F5F5F0] p-0 overflow-hidden">
        <div className="h-[3px] w-full"
              style={{ background: "linear-gradient(90deg,#D8B23E,#F6E4A6,#B87333,#F6E4A6,#D8B23E)" }} />
        <div className="p-6 space-y-5">
          <DialogHeader className="space-y-2">
            <div className="font-grotesk text-[10px] uppercase tracking-[0.24em] text-[#8A6A1A] inline-flex items-center gap-1.5">
              <ShieldCheck size={11} className="text-[#D8B23E]" /> Vinci Order Security Pulse
            </div>
            <DialogTitle className="font-serif text-2xl text-[#F5F5F0] leading-tight">
              Authorise this trade
            </DialogTitle>
            <DialogDescription className="font-body text-sm text-[#C9C9C2] leading-relaxed">
              A fresh 6-digit pulse was despatched to{" "}
              <b className="text-[#F5F5F0]" data-testid="pulse-email">{deliveryEmail}</b>.
              Enter it below to release{" "}
              <b className="text-[#D8B23E]">{actionLabel}</b>.
            </DialogDescription>
          </DialogHeader>

          <div className="flex justify-center gap-2 py-1" onPaste={onPaste}>
            {digits.map((d, i) => (
              <input key={i} ref={(el) => (inputs.current[i] = el)}
                      data-testid={`pulse-digit-${i}`}
                      value={d}
                      onChange={(e) => setDigit(i, e.target.value)}
                      onKeyDown={(e) => onKeyDown(i, e)}
                      inputMode="numeric" maxLength={1} disabled={expired}
                      className="w-11 h-14 text-center font-mono text-2xl bg-black/60 border border-[#D8B23E]/30 rounded-sm text-[#F6E4A6] focus:outline-none focus:border-[#D8B23E] focus:ring-2 focus:ring-[#D8B23E]/25 disabled:opacity-40" />
            ))}
          </div>

          <AnimatePresence>
            {expired && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                          className="text-center text-[11px] text-[#C0392B] font-grotesk uppercase tracking-[0.2em]">
                ⏳ Pulse expired · request a fresh one
              </motion.div>
            )}
          </AnimatePresence>

          <div className="flex items-center justify-between text-[11px] font-mono">
            <span className="text-[#8A8A82] inline-flex items-center gap-1.5">
              <Lock size={10} /> Expires in
              <span data-testid="pulse-timer"
                     className={expiresMs < 60_000 ? "text-[#C0392B]" : "text-[#D8B23E]"}>
                {fmtMS(expiresMs)}
              </span>
            </span>
            <button type="button" onClick={onResend} disabled={!resendReady}
                    data-testid="pulse-resend"
                    className="inline-flex items-center gap-1.5 text-[#8A6A1A] hover:text-[#D8B23E] uppercase tracking-[0.18em] text-[10px] disabled:opacity-40 disabled:hover:text-[#8A6A1A]">
              <RefreshCw size={10} />
              {resendReady ? "Resend Pulse" : `Resend in ${fmtMS(resendMs)}`}
            </button>
          </div>

          <button type="button" onClick={submit} disabled={!canSubmit}
                  data-testid="pulse-authorize"
                  className="w-full py-3 rounded-sm bg-gradient-to-r from-[#D8B23E] via-[#F6E4A6] to-[#B87333] text-black font-grotesk text-xs uppercase tracking-[0.28em] inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed">
            {verifying ? <Loader2 size={13} className="animate-spin" /> : <ShieldCheck size={13} />}
            Authorize the Trade
          </button>

          <div className="text-[10px] text-[#6f6f68] font-body leading-relaxed text-center">
            <Mail size={9} className="inline mr-1 -mt-[2px]" />
            Didn't receive it? Check spam. Every failed attempt is logged
            to the Vinci Master Ledger with your IP address.
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
